import {Card, Container, Group, Menu, rem, Stack, Text, Title, UnstyledButton} from "@mantine/core";
import {useCallback, useMemo} from "react";
import {
  adjustToTimeZoneSafely,
  extractApiErrorMessage,
  firstOrUndefined,
  InvoiceSearchCriteria,
  invoicesSelector,
  useSearchInvoicesQuery
} from "@ots-frontend/ots-data";
import {IconChevronDown, IconFileInvoice} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {skipToken} from "@reduxjs/toolkit/query";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {ResizeableContent} from "../../components/ResizableContent/ResizableContent";
import {InvoiceDataTable} from "../../components/data-table/InvoiceDataTable/InvoiceDataTable";
import {PAGINATION_PAGE_SIZES} from "../../components/data-table/DataTable/usePagination";
import {selectUIInvoiceSearchFilter} from "../../store/entity/invoice/selectors";
import {useOtsUISelector} from "../../hooks/useOtsUISelector";
import {useDispatch} from "react-redux";
import {setUIInvoiceSearchFilter} from "../../store/entity/invoice/actions";
import {RouteBreadcrumbs} from "../../components/breadcrumb/RouteBreadcrumbs/RouteBreadcrumbs";
import {notifications} from "@mantine/notifications";
import {InvoiceSearchToolbar} from "../../components/toolbar/InvoiceSearchToolbar/InvoiceSearchToolbar";

export type InvoiceSearchPageProps = {}

export const InvoiceSearchPage = (props: InvoiceSearchPageProps) => {
  const {regionId, appCategory} = useApp();
  const {t} = useTranslation(["common"]);

  const dispatch = useDispatch()

  const criteria = useOtsUISelector(selectUIInvoiceSearchFilter)

  const selectInvoices = useMemo(() => {
    return invoicesSelector();
  }, []);

  const searchArg: InvoiceSearchCriteria | typeof skipToken = useMemo(() => {
    const invoiceNumber = firstOrUndefined(criteria?.invoiceNumbers)
    if (criteria?.fromDttm || invoiceNumber) {
      return {
        ...criteria,
        fromDttm: adjustToTimeZoneSafely(criteria?.fromDttm)?.getTime(),
        toDttm: adjustToTimeZoneSafely(criteria?.toDttm)?.getTime(),
        regionIds: regionId ? [regionId] : undefined,
        categories: appCategory ? [appCategory.code] : undefined,
      }
    } else {
      return skipToken
    }
  }, [criteria, regionId, appCategory])

  const {invoices, isFetching, error, refetch} = useSearchInvoicesQuery(searchArg, {
    selectFromResult: (result) => ({
      ...result,
      invoices: selectInvoices(result, {}),
      isFetching: result.isFetching,
      error: result.error,
    })
  });

  const onFilterChange = useCallback((invoiceNumber: string | undefined, fromDate: Date | undefined, toDate: Date | undefined) => {
    if (invoiceNumber === firstOrUndefined(criteria?.invoiceNumbers) && fromDate?.getTime() === criteria?.fromDttm && toDate?.getTime() === criteria?.toDttm) {
      if (refetch) {
        refetch()
      }
    } else {
      dispatch(setUIInvoiceSearchFilter({
        ...criteria,
        invoiceNumbers: invoiceNumber ? [invoiceNumber] : undefined,
        fromDttm: fromDate?.getTime(),
        toDttm: toDate?.getTime()
      }))
    }
  }, [criteria, refetch, dispatch]);

  if (error) {
    notifications.show({
      id: "invoiceSearchError",
      title: t("common.error", "Error"),
      message: extractApiErrorMessage(error) ?? t("common.error", "Error"),
      color: "red",
      loading: false,
      autoClose: true,
    });
  } else {
    notifications.hide("invoiceSearchError")
  }

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Stack h="100%" m="sm">
                <RouteBreadcrumbs/>
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Group spacing="xs">
                      <IconFileInvoice/>
                      <Title order={4}>
                        {t("common.page.entity.invoice.search.title", "Search Invoices")}
                      </Title>
                    </Group>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              {t('common.action.plural', "Actions")}
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => refetch()}>
                          {t("actions.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                  <InvoiceSearchToolbar
                    defaultValues={{
                      invoiceNumber: firstOrUndefined(criteria?.invoiceNumbers),
                      fromDate: criteria?.fromDttm ? new Date(criteria.fromDttm) : undefined,
                      toDate: criteria?.toDttm ? new Date(criteria?.toDttm) : undefined,
                    }}
                    loading={isFetching}
                    onSubmit={onFilterChange}/>
                </Card>
                <InvoiceDataTable
                  withBorder
                  records={invoices}
                  recordsPerPageOptions={PAGINATION_PAGE_SIZES}
                  fetching={isFetching}/>
              </Stack>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
